import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/consultant-real.jpg";

export const consultants: Persona = {
  slug: "consultants-business-coaches",
  profession: { hu: "tanácsadó", en: "consultant" },
  professionPlural: {
    hu: "tanácsadók és üzleti coachok",
    en: "consultants and business coaches",
  },
  professionGenitive: { hu: "tanácsadóként", en: "as a consultant" },
  heroH1: {
    hu: "Profi megjelenés, több ügyfél, kevesebb adminisztráció.",
    en: "Professional look, more clients, less admin.",
  },
  heroSub: {
    hu: "Saját weboldal, online időpontfoglalás, csomagok és bérletek, automatikus emlékeztetők és számlázás: egy helyen, tanácsadóknak és coachoknak.",
    en: "Personal website, online booking, packages and passes, automatic reminders and invoicing: all in one place, for consultants and coaches.",
  },
  heroImage,
  heroImageAlt: "Business consultant in a client consultation",
  chatExamplePrompt: {
    hu: "Foglalj 1 órát Péterrel jövő kedd 14:00, strategy session",
    en: "Book 1 hour with Péter next Tuesday 2 PM, strategy session",
  },
  chatExampleResponse: {
    hu: "Bejegyeztem kedd 14:00, 60 perc strategy session. Péternek küldtem visszaigazolást és a Google Meet linket.",
    en: "Booked Tuesday 2 PM, 60-minute strategy session. Confirmation and Google Meet link sent to Péter.",
  },
  accentColor: "purple",

  defaultMode: "solo",
  teamSize: { min: 1, max: 12 },
  soloLabel: { hu: "Szóló tanácsadó", en: "Solo consultant" },
  teamLabel: {
    hu: "Tanácsadó csapat / agency",
    en: "Consulting team / agency",
  },

  soloDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Reggeli kávé. Az AI riportot küld: 2 új lead a weboldalról, mindketten kérnek discovery hívást.",
        en: "Morning coffee. AI report: 2 new leads from the site, both want a discovery call.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "Strategy session Péterrel. A Google Meet link automatikus, a karton már megnyitva.",
        en: "Strategy session with Péter. Google Meet link auto-generated, his profile already open.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Új csomagot hozol létre chatben: '90 napos vezetői coaching, 480 000 Ft'. 30 mp múlva élesítve.",
        en: "You create a new package in chat: '90-day executive coaching, 480,000 HUF'. Live in 30 seconds.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Egy ügyfél fizet részletben. A 3. részlet automatikusan levonódik, számla generálva.",
        en: "A client pays in installments. The 3rd installment is auto-deducted, invoice generated.",
      },
    },
    {
      time: "20:00",
      what: {
        hu: "Napzárás: 4 session, 1 új szerződés, 92 000 Ft bevétel. Heti riport péntekre.",
        en: "End of day: 4 sessions, 1 new contract, 92,000 HUF revenue. Weekly report due Friday.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Agency dashboard: 5 tanácsadód 12 sessiont ad ma. Mindenki naptára átláthatóan.",
        en: "Agency dashboard: your 5 consultants have 12 sessions today. All calendars at a glance.",
      },
    },
    {
      time: "10:30",
      what: {
        hu: "Új kliens érkezik: az AI elemzi az igényét és a megfelelő tanácsadóhoz osztja.",
        en: "New client incoming: AI analyzes their need and routes them to the right consultant.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Egy tanácsadó beteg lett. Az AI átszervezi a sessionjeit kollégákhoz, klienseket értesít.",
        en: "A consultant got sick. AI re-assigns their sessions to colleagues, notifies clients.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Az AI riportot küld: Anna 28%-kal több visszatérő klienst hoz. Megmutatod a csapatnak a folyamatait.",
        en: "AI report: Anna brings in 28% more return clients. You share her process with the team.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Hónap vége: bevétel tanácsadónként, jutalék-számítás, klient-elégedettségi pontszámok.",
        en: "Month-end: revenue per consultant, commission, client satisfaction scores.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Automatikus elszámolás Számlázz.hu-val. Bér- és jutalék-kifizetés előkészítve.",
        en: "Auto-settled with Számlázz.hu. Payroll and commission payouts ready.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: { hu: "Minden egy fiókban", en: "Everything in one account" },
      body: {
        hu: "Naptár, klient-karton, csomag, számla: egyetlen rendszerben, nincs Calendly + külön CRM + külön számlázóprogram.",
        en: "Calendar, client cards, packages, invoices: in one system, no Calendly + separate CRM + separate invoicer.",
      },
    },
    {
      icon: "Bot",
      title: { hu: "Az AI lead-eket fogad", en: "AI handles leads" },
      body: {
        hu: "Új érdeklődő: az AI a discovery hívást, az árkalkulációt és az ajánlatot is elintézi.",
        en: "New lead: AI handles the discovery call, pricing and the proposal too.",
      },
    },
    {
      icon: "CreditCard",
      title: {
        hu: "Csomagok és részletfizetés",
        en: "Packages and installments",
      },
      body: {
        hu: "Coaching csomagok automata részletfizetéssel. Levonás a megadott napon, számla azonnal.",
        en: "Coaching packages with auto-installments. Deduction on the set date, invoice immediately.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan tanácsadó, egy ár",
        en: "Unlimited consultants, one price",
      },
      body: {
        hu: "1 vagy 12 fő: ugyanaz a havidíj. Mindenki saját naptárral, klient-körrel, statisztikával.",
        en: "1 or 12 people: same monthly fee. Each with their own calendar, clients, stats.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Jogosultság-kezelés", en: "Role-based access" },
      body: {
        hu: "Owner, manager, tanácsadó: mindenki csak azt látja, amihez joga van. Bizalmas kliens-adatok védve.",
        en: "Owner, manager, consultant: everyone sees only what they're allowed to. Confidential client data is protected.",
      },
    },
    {
      icon: "Sparkles",
      title: {
        hu: "AI irányítja a beérkező érdeklődőket",
        en: "AI routes client inquiries",
      },
      body: {
        hu: "Új lead: az AI a specialitás (HR, stratégia, exec coaching) alapján a megfelelő tanácsadóhoz osztja.",
        en: "New lead: AI assigns by specialty (HR, strategy, exec coaching) to the right consultant.",
      },
    },
    {
      icon: "TrendingUp",
      title: {
        hu: "Automatikus jutalék-elszámolás",
        en: "Automatic commission settlement",
      },
      body: {
        hu: "Tanácsadónként bevétel, projekt-mérföldkő alapú kifizetés, exportálható riportok.",
        en: "Revenue per consultant, milestone-based payouts, exportable reports.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: { hu: "Smart booking", en: "Smart booking" },
      body: {
        hu: "Időzóna-kezelés automatikus, buffer time, dupla foglalás kizárva, Google Meet link generálás.",
        en: "Auto timezone handling, buffer time, no double-booking, Google Meet link generation.",
      },
    },
    {
      icon: "Briefcase",
      title: { hu: "Csomag-értékesítés", en: "Package sales" },
      body: {
        hu: "3 hónapos vezetői coaching, 90 napos turnaround program: egy oldalon, online fizetéssel.",
        en: "3-month executive coaching, 90-day turnaround program: one page, online payment.",
      },
    },
    {
      icon: "CreditCard",
      title: {
        hu: "Coaching csomagok és számlázás",
        en: "Coaching packages and invoicing",
      },
      body: {
        hu: "Egyedi árazás, online vagy átutalásos fizetés, automatikus számla foglaláskor: Számlázz.hu / Billingo, devizás számla EU-ügyfélnek.",
        en: "Custom pricing, online or bank transfer, auto-invoicing at booking: Számlázz.hu / Billingo, FX invoices for EU clients.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "Lead nurturing AI", en: "AI lead nurturing" },
      body: {
        hu: "Discovery után automata follow-up sorozat, ajánlat-időzítés, döntés-támogatás.",
        en: "Automated follow-up sequence after discovery, proposal timing, decision support.",
      },
    },
  ],

  objections: [
    {
      question: {
        hu: "A klienseim prémium szegmensből jönnek: kifejezetten személyes a kapcsolat",
        en: "My clients are premium: the relationship is very personal",
      },
      answer: {
        hu: "Pont ezért érdemes a Bespire. Az AI az adminisztrációt veszi le rólad, te 100%-ban a kliensekkel foglalkozhatsz. A klient nem találkozik a rendszerrel: ő csak a profi visszaigazolásokat, automatikus emlékeztetőket, gyors számlát látja.",
        en: "That's exactly why Bespire helps. AI takes the admin off your plate so you can focus 100% on clients. The client doesn't see the system: they see polished confirmations, reminders, fast invoices.",
      },
    },
    {
      question: {
        hu: "Csapatban dolgozunk: hogy kezeli az agencyt?",
        en: "We work as a team: how does it handle the agency?",
      },
      answer: {
        hu: "Minden tanácsadónak saját naptára, klient-listája, statisztikája van. Te tulajdonosként vagy menedzserként a centralizált dashboardon mindent látsz, beleértve a jutalék-elszámolást. Korlátlan tanácsadót vehetsz fel, NEM fizetsz többet érte.",
        en: "Each consultant has their own calendar, clients and stats. As owner or manager you see everything on the central dashboard, including commission. Add unlimited consultants: you do NOT pay more.",
      },
    },
    {
      question: {
        hu: "Bizalmas kliens-adatokat kezelünk",
        en: "We handle confidential client data",
      },
      answer: {
        hu: "EU-szerverek, GDPR-megfelelőség, end-to-end titkosítás. Jogosultság-kezeléssel csak az érintett tanácsadó látja a saját klienseit. Az adatok soha nem kerülnek 3. félhez, AI training-re sem.",
        en: "EU servers, GDPR-compliant, end-to-end encryption. Role-based access: only the assigned consultant sees their clients. Data is never shared with 3rd parties, never used for AI training.",
      },
    },
    {
      question: {
        hu: "El tudom hozni a meglévő klienseimet?",
        en: "Can I bring my existing clients?",
      },
      answer: {
        hu: "Igen. CSV/Excel importtal a teljes kliensállományt, vagy a Beüzemelés szolgáltatás keretében migráljuk a meglévő rendszeredből (HubSpot, Calendly, egyedi CRM).",
        en: "Yes. CSV/Excel import for the full base, or our Setup service migrates from your existing system (HubSpot, Calendly, custom CRM).",
      },
    },
  ],

  monetization: [
    {
      icon: "Calendar",
      title: { hu: "1:1 sessionök", en: "1:1 sessions" },
      examples: [
        { hu: "Discovery hívás", en: "Discovery call" },
        { hu: "Strategy session", en: "Strategy session" },
        { hu: "Executive coaching", en: "Executive coaching" },
      ],
      priceHint: {
        hu: "Példa: 40 session × 30 000 Ft = 1 200 000 Ft / hó (strategy 25:45e Ft)",
        en: "Example: 40 sessions × 30,000 HUF = 1,200,000 HUF / month (strategy 25:45k HUF)",
      },
    },
    {
      icon: "Briefcase",
      title: { hu: "Coaching csomagok", en: "Coaching packages" },
      examples: [
        { hu: "90 napos transzformáció", en: "90-day transformation" },
        { hu: "6 hónapos vezetői program", en: "6-month leadership program" },
        { hu: "Karrierváltás coaching", en: "Career change coaching" },
      ],
      priceHint: {
        hu: "Példa: 8 csomag × 380 000 Ft = 3 040 000 Ft / hó",
        en: "Example: 8 packages × 380,000 HUF = 3,040,000 HUF / month",
      },
    },
    {
      icon: "Users",
      title: {
        hu: "Workshopok és mastermind",
        en: "Workshops and masterminds",
      },
      examples: [
        { hu: "1 napos vezetői workshop", en: "1-day leadership workshop" },
        { hu: "Negyedéves mastermind", en: "Quarterly mastermind" },
        { hu: "Csapatépítő tréning", en: "Team-building training" },
      ],
      priceHint: {
        hu: "Példa: 1 workshop × 12 fő × 65 000 Ft = 780 000 Ft",
        en: "Example: 1 workshop × 12 people × 65,000 HUF = 780,000 HUF",
      },
    },
    {
      icon: "BookOpen",
      title: { hu: "Digitális termékek", en: "Digital products" },
      examples: [
        { hu: "Vezetői önértékelő PDF", en: "Leadership self-assessment PDF" },
        { hu: "Stratégiai sablon-csomag", en: "Strategy template bundle" },
        { hu: "Felvett kurzus", en: "Recorded course" },
      ],
      priceHint: {
        hu: "Passzív bevétel: ~250 000 Ft / hó",
        en: "Passive revenue: ~250,000 HUF / month",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Retainer és tagság", en: "Retainer and membership" },
      examples: [
        { hu: "Havi tanácsadói retainer", en: "Monthly advisory retainer" },
        { hu: "VIP mastermind tagság", en: "VIP mastermind membership" },
        { hu: "Éves szerződés", en: "Annual contract" },
      ],
      priceHint: {
        hu: "Agency-szint: 5 tanácsadó × 8 retainer × 180 000 Ft = 7 200 000 Ft / hó potenciál",
        en: "Agency level: 5 consultants × 8 retainers × 180,000 HUF = 7,200,000 HUF / month potential",
      },
    },
  ],

  results: [
    {
      value: "+58%",
      label: { hu: "lead → ügyfél konverzió", en: "lead → client conversion" },
    },
    {
      value: "2.4x",
      label: { hu: "ismétlődő bevétel", en: "recurring revenue" },
    },
    {
      value: "4.9★",
      label: { hu: "kliens-elégedettség", en: "client satisfaction" },
    },
    {
      value: "0 admin",
      label: { hu: "automatikus számlázás", en: "invoicing automated" },
    },
  ],
  testimonial: {
    quote: {
      hu: "A coaching csomagjaim eladása megnőtt, mert a kliens a weboldalon magától ki tudja választani: nem kell minden potenciális ügyféllel külön órákat egyeztetnem.",
      en: "My coaching package sales grew because clients can choose on the website themselves: I don't have to spend hours scheduling with every prospect.",
    },
    name: "Dr. Horváth Eszter",
    role: { hu: "Üzleti coach", en: "Business coach" },
    city: { hu: "Budapest, II. kerület", en: "Budapest, District II" },
    avatarInitials: "HE",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a programjaid",
        en: "Tell us your programs in chat",
      },
      body: {
        hu: "Strategy session, 90 napos coaching, vezető-fejlesztés: az AI strukturálja.",
        en: "Strategy session, 90-day coaching, leadership development: AI structures it.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti a coaching oldalt és csomagokat",
        en: "AI builds your coaching site and packages",
      },
      body: {
        hu: "Részletfizetés, NDA, kérdőív foglaláskor.",
        en: "Installments, NDA, intake questionnaire at booking.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Migráljuk a meglévő klienseidet",
        en: "We migrate your existing clients",
      },
      body: {
        hu: "Korábbi session-jegyzetekkel, szerződésekkel.",
        en: "With past session notes and contracts.",
      },
    },
    {
      step: 4,
      title: {
        hu: "Élő foglalás és automata lead-kezelés",
        en: "Live bookings and automated lead handling",
      },
      body: {
        hu: "Discovery call űrlap, automatikus ütemezés, follow-up email.",
        en: "Discovery call form, auto-scheduling, follow-up email.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Outlook",
    "Stripe",
    "SimplePay",
    "Google Meet",
    "DocuSign",
    "Mailchimp",
    "LinkedIn",
  ],
  breadcrumbLabel: {
    hu: "Tanácsadók és üzleti coachok",
    en: "Consultants & business coaches",
  },
  similarPersonas: ["therapists-coaches", "psychologists-mentors"],
};
